// Fuzzball Quiz Utilities
// Shared utility functions for DOM manipulation, timing, and helpers

window.QuizUtils = {
  // DOM Utilities
  DOM: {
    // Wait for element to appear in DOM
    waitForElement(selector, timeout = 5000) {
      return new Promise((resolve, reject) => {
        const element = document.querySelector(selector);
        if (element) {
          resolve(element);
          return;
        }

        const observer = new MutationObserver((mutations, obs) => {
          const el = document.querySelector(selector);
          if (el) {
            obs.disconnect();
            resolve(el);
          }
        });

        observer.observe(document.body, {
          childList: true,
          subtree: true
        });

        setTimeout(() => {
          observer.disconnect();
          reject(new Error(`Timeout waiting for element: ${selector}`));
        }, timeout);
      });
    },

    // Update multiple elements with same selector
    updateMultiple(selector, value, property = 'textContent') {
      const elements = document.querySelectorAll(selector);
      elements.forEach(el => {
        el[property] = value;
      });
    },

    // Show/hide element with optional animation
    show(element, display = 'block') {
      if (typeof element === 'string') {
        element = document.querySelector(element);
      }
      if (element) {
        element.style.display = display;
      }
    },

    hide(element) {
      if (typeof element === 'string') {
        element = document.querySelector(element);
      }
      if (element) {
        element.style.display = 'none';
      }
    },

    // Add/remove classes safely
    addClass(element, className) {
      if (typeof element === 'string') {
        element = document.querySelector(element);
      }
      if (element) {
        element.classList.add(className);
      }
    },

    removeClass(element, className) {
      if (typeof element === 'string') {
        element = document.querySelector(element);
      }
      if (element) {
        element.classList.remove(className);
      }
    },

    // Check if element has class
    hasClass(element, className) {
      if (typeof element === 'string') {
        element = document.querySelector(element);
      }
      return element ? element.classList.contains(className) : false;
    },

    // Lock/unlock body scroll
    lockBodyScroll() {
      document.body.classList.add('overflow-hidden');
    },

    unlockBodyScroll() {
      document.body.classList.remove('overflow-hidden');
    }
  },

  // Timing Utilities
  Timing: {
    // Debounce function calls
    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    },

    // Throttle function calls
    throttle(func, limit) {
      let inThrottle;
      return function(...args) {
        if (!inThrottle) {
          func.apply(this, args);
          inThrottle = true;
          setTimeout(() => inThrottle = false, limit);
        }
      };
    },

    // Wait for condition to be true
    waitFor(condition, interval = 200, timeout = 5000) {
      return new Promise((resolve, reject) => {
        const startTime = Date.now();
        
        const check = () => {
          if (condition()) {
            resolve();
          } else if (Date.now() - startTime > timeout) {
            reject(new Error('Timeout waiting for condition'));
          } else {
            setTimeout(check, interval);
          }
        };
        
        check();
      });
    },

    // Simple delay
    delay(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    }
  },

  // Formatting Utilities
  Format: {
    // Format price
    money(cents, currency = '£') {
      return `${currency}${(cents / 100).toFixed(2)}`;
    },

    // Format cat name with fallback
    catName(name) {
      return name || 'your cat';
    },

    // Pluralize text
    pluralize(count, singular, plural = null) {
      if (count === 1) return singular;
      return plural || `${singular}s`;
    },

    // Format delivery text
    deliveryText(bags, cans) {
      const parts = [];
      
      if (bags > 0) {
        parts.push(`${bags} ${this.pluralize(bags, 'bag')}`);
      }
      
      if (cans > 0) {
        parts.push(`${cans} ${this.pluralize(cans, 'tin')}`);
      }
      
      return parts.length > 0 ? parts.join(', ') + ' in total' : '0 items in total';
    }
  },

  // Data Utilities
  Data: {
    // Deep clone object
    deepClone(obj) {
      return JSON.parse(JSON.stringify(obj));
    },

    // Merge objects deeply
    deepMerge(target, source) {
      const output = Object.assign({}, target);
      if (this.isObject(target) && this.isObject(source)) {
        Object.keys(source).forEach(key => {
          if (this.isObject(source[key])) {
            if (!(key in target)) {
              Object.assign(output, { [key]: source[key] });
            } else {
              output[key] = this.deepMerge(target[key], source[key]);
            }
          } else {
            Object.assign(output, { [key]: source[key] });
          }
        });
      }
      return output; 
    },

    // Check if value is object
    isObject(item) {
      return item && typeof item === 'object' && !Array.isArray(item);
    },

    // Remove duplicates from array
    unique(array, key) {
      if (key) {
        const seen = new Set();
        return array.filter(item => {
          const val = item[key];
          if (seen.has(val)) {
            return false;
          }
          seen.add(val);
          return true;
        });
      }
      return [...new Set(array)];
    },

    // Base64 encode/decode
    encode(str) {
      return btoa(JSON.stringify(str));
    },

    decode(str) {
      try {
        return JSON.parse(atob(str));
      } catch (e) {
        return null;
      }
    }
  },

  // Validation Utilities
  Validation: {
    // Check if email is valid
    isValidEmail(email) {
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return re.test(email);
    },

    // Check if value is empty
    isEmpty(value) {
      if (value === null || value === undefined || value === '') return true;
      if (Array.isArray(value) && value.length === 0) return true;
      if (typeof value === 'object' && Object.keys(value).length === 0) return true;
      return false;
    },

    // Check if number is in range
    inRange(value, min, max) {
      const num = parseFloat(value);
      return !isNaN(num) && num >= min && num <= max;
    }
  },

  // Browser Utilities
  Browser: {
    // Get URL parameters
    getUrlParams() {
      const params = new URLSearchParams(window.location.search);
      const result = {};
      for (const [key, value] of params) {
        result[key] = value;
      }
      return result;
    },

    // Update URL without reload
    updateUrl(params) {
      const url = new URL(window.location);
      Object.entries(params).forEach(([key, value]) => {
        if (value === null || value === undefined) {
          url.searchParams.delete(key);
        } else {
          url.searchParams.set(key, value);
        }
      });
      window.history.pushState({}, '', url);
    },

    // Copy to clipboard
    copyToClipboard(text) {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
  },

  // Error Handling Utilities
  Error: {
    // Log error with context
    log(message, error, context = {}) {
      console.error(`Quiz Error: ${message}`, {
        error: error,
        context: context,
        timestamp: new Date().toISOString()
      });
    },

    // Show user-friendly error
    show(message, duration = 5000) {
      // Create error element if it doesn't exist
      let errorEl = document.querySelector('.quiz-error-message');
      if (!errorEl) {
        errorEl = document.createElement('div');
        errorEl.className = 'quiz-error-message';
        errorEl.style.cssText = `
          position: fixed;
          top: 20px;
          right: 20px;
          background: #ef4444;
          color: white;
          padding: 16px 20px;
          border-radius: 8px;
          z-index: 10000;
          font-weight: 600;
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
          display: none;
        `;
        document.body.appendChild(errorEl);
      }

      errorEl.textContent = message;
      errorEl.style.display = 'block';

      setTimeout(() => {
        errorEl.style.display = 'none';
      }, duration);
    }
  },

  // Success Message Utilities
  Success: {
    show(message, duration = 3000) {
      const successEl = document.createElement('div');
      successEl.className = 'quiz-success-message';
      successEl.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: #22c55e;
        color: white;
        padding: 16px 20px;
        border-radius: 8px;
        z-index: 10000;
        font-weight: 600;
        box-shadow: 0 4px 12px rgba(0,0,0,0.1);
      `;
      successEl.innerHTML = `✅ ${message}`;
      
      document.body.appendChild(successEl);
      
      setTimeout(() => {
        if (successEl.parentNode) {
          successEl.parentNode.removeChild(successEl);
        }
      }, duration);
    }
  }
};